import moment from 'moment'
import type React from 'react'
import { useState } from 'react'
import styled from 'styled-components'

import LandlordRentDeductions from '@/components/Landlord/LandlordRentDeductions'
import VerifyButton from '@/components/VerifyButton'
import type { Address, Application } from '@/types/Activity'
import type { ILandlordPropertyReference } from '@/types/Landlord'
import { formatGBP } from '@/utils/helpers'

import LandlordStatusDescription from './LandlordStatusDescription'
import LandlordStatusPill from './LandlordStatusPill'
import SubmitLandlordReferenceForm from './SubmitLandlordReferenceForm'

interface Props {
  application: Application
  reference: ILandlordPropertyReference
  subjectId?: string
  address: Address
}

const Container = styled.div``

const DateContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const LandlordPropertyReference: React.FC<Props> = ({
  reference,
  subjectId,
  application,
  address,
}) => {
  const [verifyPanelOpen, setVerifyPanelOpen] = useState(false)
  const tenantReference = reference.tenants.find(({ id }) => id === subjectId)
  const primaryTenantReference = reference.tenants?.[0]
  const tenantStatus =
    subjectId !== undefined
      ? tenantReference?.outcome?.status
      : primaryTenantReference?.outcome?.status
  const hasSubmittedStatus = tenantStatus !== undefined && tenantStatus !== 'Pending'
  const isCompletedStatus = hasSubmittedStatus && tenantStatus !== 'Expired'

  function getSubjectNameFromId(id: string) {
    if (!application.attachedSubjects) return ''
    return application.attachedSubjects.find(s => s.subjectId === id)?.name
  }

  return (
    <>
      {verifyPanelOpen && (
        <SubmitLandlordReferenceForm
          setOpen={setVerifyPanelOpen}
          reference={reference}
          application={application}
        />
      )}
      <Container>
        <div className="side-by-side-flex-end">
          {tenantReference && (
            <LandlordStatusPill label={tenantReference?.outcome.status} />
          )}
        </div>{' '}
        {reference.submittedBy && isCompletedStatus && (
          <div>
            This reference has been completed by{' '}
            {reference.submittedBy.type === 'Landlord'
              ? 'the landlord'
              : 'referencing team member'}{' '}
            <strong>
              {reference.submittedBy.authorName} - ({reference.submittedBy.authorEmail})
            </strong>
          </div>
        )}
        <div>
          {tenantReference && (
            <LandlordStatusDescription label={tenantReference?.outcome.status} />
          )}{' '}
        </div>
        {tenantReference && (
          <div className="side-by-side-flex-end">
            <VerifyButton
              onClick={() => setVerifyPanelOpen(true)}
              disabled={
                address.verification !== null &&
                address.verification !== undefined &&
                address.verification.state !== 'notVerified'
              }
            />
          </div>
        )}
        {tenantReference?.outcome.comments && (
          <div>
            <ul>
              {tenantReference.outcome.comments.map((comment, i) => (
                <li key={i}>{comment}</li>
              ))}
            </ul>
          </div>
        )}
        {reference.validPersonForReference !== false && reference.term && (
          <DateContainer>
            <div>
              <h4>Start Date</h4>
              <p> {moment(reference.term.startDate).format('Do MMMM YYYY')}</p>
            </div>
            {reference.term.knownEndDate && (
              <div>
                <h4>End Date</h4>
                <p> {moment(reference.term.endDate).format('Do MMMM YYYY')}</p>{' '}
              </div>
            )}
            {reference.term.knownEndDate === false &&
              reference.term.numberOfWeeksNotice && (
                <div>
                  <h4>Weeks Notice</h4>
                  {reference.term.numberOfWeeksNotice} Weeks
                </div>
              )}
          </DateContainer>
        )}
        {hasSubmittedStatus && (
          <>
            {reference.term?.endOfTenancyConfirmation && (
              <div>
                <h4>Tenancy Overlap</h4>
                {reference.term.endOfTenancyConfirmation.noticeGiven === false && (
                  <p>Landlord declines notice was given by Tenant</p>
                )}
                {reference.term.endOfTenancyConfirmation.noticeGiven === true && (
                  <p>Landlord confirms Tenant has given notice</p>
                )}
                {reference.term.endOfTenancyConfirmation.canBeLeftEarly === false && (
                  <p>Landlord declines Tenant can leave early</p>
                )}
                {reference.term.endOfTenancyConfirmation.canBeLeftEarly === true && (
                  <p>Landlord confirms Tenant can leave early </p>
                )}
              </div>
            )}
            {reference.validPersonForReference !== false && (
              <LandlordRentDeductions status={reference.rentDeductions} />
            )}
            <div className="two-column-flex">
              <div>
                {reference.validPersonForReference !== false && (
                  <div>
                    <h4>Rental Payments</h4>
                    {reference.rentWasPaidLate === false && (
                      <p>
                        The landlord has indicated that the rent was always paid on time.
                      </p>
                    )}
                    {reference.rentWasPaidLate === true && (
                      <p>
                        The landlord has indicated that the rental payments were late by 2
                        weeks or more on{' '}
                        <strong>{reference.numberOfTimesRentWasLate} occasions</strong>.
                      </p>
                    )}
                  </div>
                )}
                {reference.monthlyRent != null && (
                  <div>
                    <h4>Monthly Rent</h4>
                    <p>{formatGBP(reference.monthlyRent)}</p>
                  </div>
                )}
              </div>
              <div>
                {reference.propertyLeftInGoodCondition != null && (
                  <div>
                    <h4>Property Left in Good Condition</h4>
                    <p>
                      {reference.propertyLeftInGoodCondition
                        ? 'Yes'
                        : `No${
                            reference.propertyConditionDetails
                              ? ` — ${reference.propertyConditionDetails}`
                              : ''
                          }`}
                    </p>
                  </div>
                )}
                {reference.wouldRentToTenantAgain != null && (
                  <div>
                    <h4>Would Rent to Tenant Again</h4>
                    <p>
                      {reference.wouldRentToTenantAgain
                        ? 'Yes'
                        : `No${
                            reference.wouldRentToTenantAgainDetails
                              ? ` — ${reference.wouldRentToTenantAgainDetails}`
                              : ''
                          }`}
                    </p>
                  </div>
                )}
              </div>
            </div>
            {subjectId === undefined && (
              <div>
                <h4>{reference.tenants.length} Tenant(s)</h4>
                {reference.tenants.map((tenant, i) => (
                  <div key={i}>
                    <div className="side-by-side-flex">
                      <h4>{getSubjectNameFromId(tenant.id)}</h4>
                      <LandlordStatusPill label={tenant.outcome.status} />
                    </div>
                    {tenant.outcome.comments && (
                      <div>
                        <ul>
                          {tenant.outcome.comments.map(comment => (
                            <li>{comment}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </Container>
    </>
  )
}

export default LandlordPropertyReference
